import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Save, Eye, ArrowLeft, Plus, Trash2, GripVertical, 
  Type, Heading, Image, Square, Columns, Minus, Code, Target,
  LayoutGrid, Box, AlignVerticalSpaceAround, Maximize, PanelTop, Copy, DollarSign, PanelLeft
} from 'lucide-react';
import PageSidebarSettings from '../../components/PageSidebarSettings';

// Component types available in the builder
const COMPONENT_TYPES = [
  { type: 'heading', label: 'Heading', icon: Heading, category: 'content' },
  { type: 'text', label: 'Text Block', icon: Type, category: 'content' },
  { type: 'image', label: 'Image', icon: Image, category: 'content' },
  { type: 'button', label: 'Button', icon: Square, category: 'content' },
  { type: 'html', label: 'HTML Code', icon: Code, category: 'content' },
  { type: 'tool', label: 'Tool Plugin', icon: Target, category: 'content' },
  { type: 'ad', label: 'Ad Block', icon: DollarSign, category: 'content' },
  { type: 'divider', label: 'Divider', icon: Minus, category: 'layout' },
  { type: 'spacer', label: 'Spacer', icon: AlignVerticalSpaceAround, category: 'layout' },
  { type: 'section', label: 'Section', icon: PanelTop, category: 'layout' },
  { type: 'container', label: 'Container', icon: Box, category: 'layout' },
  { type: 'columns', label: 'Columns', icon: LayoutGrid, category: 'layout' },
];

// Nested component types that can go inside columns
const NESTED_COMPONENT_TYPES = [
  { type: 'heading', label: 'Heading', icon: Heading },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'button', label: 'Button', icon: Square },
];

// Column Drop Zone for nested components
function ColumnDropZone({ column, columnIndex, parentComponent, onUpdate, layout }) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  
  const getColSpan = () => {
    if (layout === '1-2') return columnIndex === 0 ? 'col-span-1' : 'col-span-1';
    if (layout === '2-1') return columnIndex === 0 ? 'col-span-1' : 'col-span-1';
    if (layout === '1-1-2') return columnIndex === 2 ? 'col-span-2' : 'col-span-1';
    return 'col-span-1';
  };
  
  const addComponentToColumn = (type) => {
    const newComp = {
      id: `nested_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      content: type === 'heading' ? { text: 'New Heading', level: 'h3', align: 'left' } :
               type === 'text' ? { text: 'Add your text here...', align: 'left' } :
               type === 'button' ? { text: 'Click Me', url: '#', style: 'primary', align: 'left' } :
               type === 'image' ? { src: '', alt: '', width: 'full' } : {},
      style: {}
    };
    
    const updatedColumns = [...(parentComponent.content.columns || [])];
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      components: [...(updatedColumns[columnIndex].components || []), newComp]
    };
    
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: { ...parentComponent.content, columns: updatedColumns }
    });
    setShowAddMenu(false);
  };
  
  const updateNestedComponent = (compId, updatedComp) => {
    const updatedColumns = [...(parentComponent.content.columns || [])];
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      components: updatedColumns[columnIndex].components.map(c => 
        c.id === compId ? updatedComp : c
      )
    };
    
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: { ...parentComponent.content, columns: updatedColumns }
    });
  };
  
  const deleteNestedComponent = (compId) => {
    const updatedColumns = [...(parentComponent.content.columns || [])];
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      components: updatedColumns[columnIndex].components.filter(c => c.id !== compId)
    };
    
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: { ...parentComponent.content, columns: updatedColumns }
    });
  };
  
  const moveComponent = (fromIndex, toIndex) => {
    const updatedColumns = [...(parentComponent.content.columns || [])];
    const components = [...updatedColumns[columnIndex].components];
    const [moved] = components.splice(fromIndex, 1);
    components.splice(toIndex, 0, moved);
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      components
    };
    
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: { ...parentComponent.content, columns: updatedColumns }
    });
  };
  
  return (
    <div className={`${getColSpan()} min-h-[60px] bg-slate-900/50 border border-dashed border-slate-600 rounded p-2`}>
      <div className="text-[10px] text-slate-500 mb-1 text-center">Col {columnIndex + 1}</div>
      
      {/* Nested Components */}
      {(column.components || []).map((nestedComp, idx) => (
        <div key={nestedComp.id} className="mb-2 bg-slate-700 rounded p-2 relative group">
          <div className="flex items-center gap-1 mb-1">
            <div className="flex gap-0.5">
              {idx > 0 && (
                <button 
                  onClick={() => moveComponent(idx, idx - 1)}
                  className="text-slate-400 hover:text-white text-xs p-0.5"
                >
                  ↑
                </button>
              )}
              {idx < column.components.length - 1 && (
                <button 
                  onClick={() => moveComponent(idx, idx + 1)}
                  className="text-slate-400 hover:text-white text-xs p-0.5"
                >
                  ↓
                </button>
              )}
            </div>
            <span className="text-[10px] text-slate-400 flex-1">{nestedComp.type}</span>
            <button 
              onClick={() => deleteNestedComponent(nestedComp.id)}
              className="text-slate-400 hover:text-red-400 p-0.5"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <NestedComponentEditor 
            component={nestedComp} 
            onUpdate={updateNestedComponent}
          />
        </div>
      ))}
      
      {/* Add Component Button */}
      <div className="relative">
        <button 
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="w-full py-1 text-[10px] text-slate-400 hover:text-white hover:bg-slate-700 rounded border border-dashed border-slate-600 flex items-center justify-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
        
        {showAddMenu && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded shadow-lg z-50">
            {NESTED_COMPONENT_TYPES.map(item => (
              <button
                key={item.type}
                onClick={() => addComponentToColumn(item.type)}
                className="w-full px-2 py-1.5 text-left text-xs text-slate-300 hover:bg-slate-700 flex items-center gap-2"
              >
                <item.icon className="w-3 h-3" />
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Section Drop Zone for nested components (similar to columns but single container)
function SectionDropZone({ parentComponent, onUpdate }) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  
  const addComponentToSection = (type) => {
    const newComp = {
      id: `nested_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      content: type === 'heading' ? { text: 'New Heading', level: 'h2', align: 'center' } :
               type === 'text' ? { text: 'Add your text here...', align: 'center' } :
               type === 'button' ? { text: 'Click Me', url: '#', style: 'primary', align: 'center', bgColor: '#2563eb', textColor: '#ffffff' } :
               type === 'image' ? { src: '', alt: '', width: 'full' } : {},
      style: {}
    };
    
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: { 
        ...parentComponent.content, 
        children: [...(parentComponent.content.children || []), newComp]
      }
    });
    setShowAddMenu(false);
  };
  
  const updateNestedComponent = (compId, updatedComp) => {
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: {
        ...parentComponent.content,
        children: (parentComponent.content.children || []).map(c => 
          c.id === compId ? updatedComp : c
        )
      }
    });
  };
  
  const deleteNestedComponent = (compId) => {
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: {
        ...parentComponent.content,
        children: (parentComponent.content.children || []).filter(c => c.id !== compId)
      }
    });
  };
  
  const moveComponent = (fromIndex, toIndex) => {
    const children = [...(parentComponent.content.children || [])];
    const [moved] = children.splice(fromIndex, 1);
    children.splice(toIndex, 0, moved);
    
    onUpdate(parentComponent.id, {
      ...parentComponent,
      content: { ...parentComponent.content, children }
    });
  };
  
  const children = parentComponent.content.children || [];
  
  return (
    <div className="border border-dashed border-slate-500 rounded-lg p-2 bg-slate-800 mt-2">
      <p className="text-xs text-slate-400 mb-2 text-center">Section Content</p>
      
      {/* Nested Components */}
      {children.map((nestedComp, idx) => (
        <div key={nestedComp.id} className="mb-2 bg-slate-700 rounded p-2 relative">
          <div className="flex items-center gap-1 mb-1">
            <div className="flex gap-0.5">
              {idx > 0 && (
                <button 
                  onClick={() => moveComponent(idx, idx - 1)}
                  className="text-slate-400 hover:text-white text-xs p-0.5"
                >
                  ↑
                </button>
              )}
              {idx < children.length - 1 && (
                <button 
                  onClick={() => moveComponent(idx, idx + 1)}
                  className="text-slate-400 hover:text-white text-xs p-0.5"
                >
                  ↓
                </button>
              )}
            </div>
            <span className="text-[10px] text-slate-400 flex-1">{nestedComp.type}</span>
            <button 
              onClick={() => deleteNestedComponent(nestedComp.id)}
              className="text-slate-400 hover:text-red-400 p-0.5"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <NestedComponentEditor 
            component={nestedComp} 
            onUpdate={updateNestedComponent}
          />
        </div>
      ))}
      
      {/* Add Component Button */}
      <div className="relative">
        <button 
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="w-full py-1.5 text-xs text-slate-400 hover:text-white hover:bg-slate-700 rounded border border-dashed border-slate-600 flex items-center justify-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add Content
        </button>
        
        {showAddMenu && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded shadow-lg z-50">
            {NESTED_COMPONENT_TYPES.map(item => (
              <button
                key={item.type}
                onClick={() => addComponentToSection(item.type)}
                className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-700 flex items-center gap-2"
              >
                <item.icon className="w-3 h-3" />
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Simplified nested component editor
function NestedComponentEditor({ component, onUpdate }) {
  const updateContent = (key, value) => {
    onUpdate(component.id, {
      ...component,
      content: { ...component.content, [key]: value }
    });
  };
  
  switch (component.type) {
    case 'heading':
      return (
        <div className="space-y-1">
          <Input
            value={component.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Heading..."
            className="bg-slate-600 border-slate-500 text-white text-xs h-7"
          />
          <div className="flex gap-1">
            <Select value={component.content.level || 'h3'} onValueChange={(val) => updateContent('level', val)}>
              <SelectTrigger className="w-14 h-6 bg-slate-600 border-slate-500 text-white text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
                <SelectItem value="h4">H4</SelectItem>
              </SelectContent>
            </Select>
            <Select value={component.content.align || 'left'} onValueChange={(val) => updateContent('align', val)}>
              <SelectTrigger className="w-16 h-6 bg-slate-600 border-slate-500 text-white text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <input
                type="color"
                value={component.content.color || '#1e293b'}
                onChange={(e) => updateContent('color', e.target.value)}
                className="w-6 h-6 rounded cursor-pointer border-0"
                title="Text Color"
              />
            </div>
          </div>
        </div>
      );
      
    case 'text':
      return (
        <div className="space-y-1">
          <Textarea
            value={component.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Text content..."
            className="bg-slate-600 border-slate-500 text-white text-xs min-h-[50px]"
          />
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400">Color:</span>
            <input
              type="color"
              value={component.content.color || '#475569'}
              onChange={(e) => updateContent('color', e.target.value)}
              className="w-6 h-6 rounded cursor-pointer border-0"
              title="Text Color"
            />
          </div>
        </div>
      );
      
    case 'image':
      return (
        <div className="space-y-1">
          <Input
            value={component.content.src || ''}
            onChange={(e) => updateContent('src', e.target.value)}
            placeholder="Image URL..."
            className="bg-slate-600 border-slate-500 text-white text-xs h-7"
          />
          <Input
            value={component.content.alt || ''}
            onChange={(e) => updateContent('alt', e.target.value)}
            placeholder="Alt text..."
            className="bg-slate-600 border-slate-500 text-white text-xs h-7"
          />
        </div>
      );
      
    case 'button':
      return (
        <div className="space-y-1">
          <Input
            value={component.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Button text..."
            className="bg-slate-600 border-slate-500 text-white text-xs h-7"
          />
          <Input
            value={component.content.url || ''}
            onChange={(e) => updateContent('url', e.target.value)}
            placeholder="Link URL..."
            className="bg-slate-600 border-slate-500 text-white text-xs h-7"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={component.content.size || 'md'} onValueChange={(val) => updateContent('size', val)}>
              <SelectTrigger className="w-16 h-6 bg-slate-600 border-slate-500 text-white text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="xs">XS</SelectItem>
                <SelectItem value="sm">SM</SelectItem>
                <SelectItem value="md">MD</SelectItem>
                <SelectItem value="lg">LG</SelectItem>
                <SelectItem value="xl">XL</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-400">BG:</span>
              <input
                type="color"
                value={component.content.bgColor || '#2563eb'}
                onChange={(e) => updateContent('bgColor', e.target.value)}
                className="w-5 h-5 rounded cursor-pointer border-0"
                title="Background Color"
              />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-400">Text:</span>
              <input
                type="color"
                value={component.content.textColor || '#ffffff'}
                onChange={(e) => updateContent('textColor', e.target.value)}
                className="w-5 h-5 rounded cursor-pointer border-0"
                title="Text Color"
              />
            </div>
          </div>
        </div>
      );
      
    default:
      return <span className="text-xs text-slate-400">Unknown type</span>;
  }
}

// Sortable component wrapper
function SortableComponent({ component, onUpdate, onDelete, onDuplicate, isActive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: component.id 
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <Card className={`bg-slate-700 border-slate-600 ${isDragging ? 'ring-2 ring-blue-500 shadow-xl' : ''} ${isActive ? 'ring-2 ring-blue-400' : ''}`}>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <div 
              {...attributes} 
              {...listeners} 
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded cursor-grab active:cursor-grabbing touch-none"
            >
              <GripVertical className="w-4 h-4" />
            </div>
            
            <div className="flex-1 min-w-0">
              <ComponentEditor component={component} onUpdate={onUpdate} />
            </div>
            
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(component);
              }} 
              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-600 rounded"
              title="Duplicate Component"
            >
              <Copy className="w-4 h-4" />
            </button>
            
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onDelete(component.id);
              }} 
              className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600 rounded"
              title="Delete Component"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Component editor based on type
function ComponentEditor({ component, onUpdate }) {
  const updateContent = (key, value) => {
    onUpdate(component.id, {
      ...component,
      content: { ...component.content, [key]: value }
    });
  };

  const updateStyle = (key, value) => {
    onUpdate(component.id, {
      ...component,
      style: { ...component.style, [key]: value }
    });
  };

  switch (component.type) {
    case 'heading':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Heading className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-medium text-slate-400">Heading</span>
          </div>
          <Input
            value={component.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Enter heading text..."
            className="bg-slate-600 border-slate-500 text-white"
          />
          <div className="flex gap-2 items-center">
            <Select
              value={component.content.level || 'h2'}
              onValueChange={(val) => updateContent('level', val)}
            >
              <SelectTrigger className="w-20 bg-slate-600 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="h1">H1</SelectItem>
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
                <SelectItem value="h4">H4</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={component.content.align || 'left'}
              onValueChange={(val) => updateContent('align', val)}
            >
              <SelectTrigger className="w-24 bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Align" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">Color:</span>
              <input
                type="color"
                value={component.content.color || '#0f172a'}
                onChange={(e) => updateContent('color', e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border-0"
                title="Text Color"
              />
            </div>
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Type className="w-4 h-4 text-green-400" />
            <span className="text-xs font-medium text-slate-400">Text Block</span>
          </div>
          <Textarea
            value={component.content.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Enter text content..."
            className="bg-slate-600 border-slate-500 text-white min-h-[80px]"
          />
          <div className="flex gap-2 items-center">
            <Select
              value={component.content.align || 'left'}
              onValueChange={(val) => updateContent('align', val)}
            >
              <SelectTrigger className="w-24 bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Align" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">Color:</span>
              <input
                type="color"
                value={component.content.color || '#475569'}
                onChange={(e) => updateContent('color', e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border-0"
                title="Text Color"
              />
            </div>
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Image className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-medium text-slate-400">Image</span>
          </div>
          <Input
            value={component.content.src || ''}
            onChange={(e) => updateContent('src', e.target.value)}
            placeholder="Image URL..."
            className="bg-slate-600 border-slate-500 text-white"
          />
          <Input
            value={component.content.alt || ''}
            onChange={(e) => updateContent('alt', e.target.value)}
            placeholder="Alt text..."
            className="bg-slate-600 border-slate-500 text-white"
          />
          <div className="flex gap-2">
            <Select
              value={component.content.width || 'full'}
              onValueChange={(val) => updateContent('width', val)}
            >
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Width" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="full">Full Width</SelectItem>
                <SelectItem value="3/4">75%</SelectItem>
                <SelectItem value="1/2">50%</SelectItem>
                <SelectItem value="1/3">33%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'button':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Square className="w-4 h-4 text-orange-400" />
            <span className="text-xs font-medium text-slate-400">Button</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={component.content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              placeholder="Button text..."
              className="bg-slate-600 border-slate-500 text-white"
            />
            <Input
              value={component.content.url || ''}
              onChange={(e) => updateContent('url', e.target.value)}
              placeholder="URL..."
              className="bg-slate-600 border-slate-500 text-white"
            />
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select
              value={component.content.align || 'left'}
              onValueChange={(val) => updateContent('align', val)}
            >
              <SelectTrigger className="w-24 bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Align" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={component.content.size || 'md'}
              onValueChange={(val) => updateContent('size', val)}
            >
              <SelectTrigger className="w-24 bg-slate-600 border-slate-500 text-white">
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="xs">XS</SelectItem>
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
                <SelectItem value="xl">XL</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">BG:</span>
              <input
                type="color"
                value={component.content.bgColor || '#2563eb'}
                onChange={(e) => updateContent('bgColor', e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border-0"
                title="Background Color"
              />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">Text:</span>
              <input
                type="color"
                value={component.content.textColor || '#ffffff'}
                onChange={(e) => updateContent('textColor', e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border-0"
                title="Text Color"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Border Radius</label>
              <Select
                value={component.content.rounded || 'full'}
                onValueChange={(val) => updateContent('rounded', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                  <SelectItem value="full">Full (Pill)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Margin Top</label>
              <Select
                value={component.content.marginTop || 'none'}
                onValueChange={(val) => updateContent('marginTop', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );

    case 'divider':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Minus className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-400">Divider</span>
          </div>
          <div className="flex gap-2">
            <Select
              value={component.content.style || 'solid'}
              onValueChange={(val) => updateContent('style', val)}
            >
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="dashed">Dashed</SelectItem>
                <SelectItem value="dotted">Dotted</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={component.content.width || 'full'}
              onValueChange={(val) => updateContent('width', val)}
            >
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="full">Full</SelectItem>
                <SelectItem value="3/4">75%</SelectItem>
                <SelectItem value="1/2">50%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'spacer':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <AlignVerticalSpaceAround className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-400">Spacer</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Height:</span>
            <Select
              value={component.content.size || 'md'}
              onValueChange={(val) => updateContent('size', val)}
            >
              <SelectTrigger className="w-32 bg-slate-600 border-slate-500 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="xs">XS (8px)</SelectItem>
                <SelectItem value="sm">SM (16px)</SelectItem>
                <SelectItem value="md">MD (32px)</SelectItem>
                <SelectItem value="lg">LG (64px)</SelectItem>
                <SelectItem value="xl">XL (96px)</SelectItem>
                <SelectItem value="2xl">2XL (128px)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'section':
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <PanelTop className="w-4 h-4 text-indigo-400" />
            <span className="text-xs font-medium text-slate-400">Section (Full Width Container)</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Background</label>
              <Select
                value={component.content.bgType || 'none'}
                onValueChange={(val) => updateContent('bgType', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="light">Light Gray</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="primary">Primary Blue</SelectItem>
                  <SelectItem value="gradient">Gradient</SelectItem>
                  <SelectItem value="custom">Custom Color</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Padding</label>
              <Select
                value={component.content.padding || 'md'}
                onValueChange={(val) => updateContent('padding', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                  <SelectItem value="xl">Extra Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {component.content.bgType === 'custom' && (
            <Input
              value={component.content.bgColor || ''}
              onChange={(e) => updateContent('bgColor', e.target.value)}
              placeholder="#hex or rgb()"
              className="bg-slate-600 border-slate-500 text-white"
            />
          )}
          
          {/* Nested Content */}
          <SectionDropZone 
            parentComponent={component}
            onUpdate={onUpdate}
          />
        </div>
      );

    case 'container':
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Box className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-medium text-slate-400">Container (Boxed Content)</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Max Width</label>
              <Select
                value={component.content.maxWidth || 'lg'}
                onValueChange={(val) => updateContent('maxWidth', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="sm">Small (640px)</SelectItem>
                  <SelectItem value="md">Medium (768px)</SelectItem>
                  <SelectItem value="lg">Large (1024px)</SelectItem>
                  <SelectItem value="xl">XL (1280px)</SelectItem>
                  <SelectItem value="full">Full Width</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Padding</label>
              <Select
                value={component.content.padding || 'md'}
                onValueChange={(val) => updateContent('padding', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Background</label>
              <Select
                value={component.content.bgType || 'none'}
                onValueChange={(val) => updateContent('bgType', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="white">White</SelectItem>
                  <SelectItem value="light">Light Gray</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Border Radius</label>
              <Select
                value={component.content.rounded || 'none'}
                onValueChange={(val) => updateContent('rounded', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );

    case 'columns':
      const getColumnCount = (layout) => {
        switch (layout) {
          case '3-equal': return 3;
          case '4-equal': return 4;
          case '1-1-2': return 3;
          default: return 2;
        }
      };
      
      const columnCount = getColumnCount(component.content.layout || '2-equal');
      
      // Initialize columns array if not exists
      if (!component.content.columns) {
        const initialColumns = Array(columnCount).fill(null).map((_, i) => ({
          id: `col_${i}_${Date.now()}`,
          components: []
        }));
        // Use a timeout to avoid state update during render
        setTimeout(() => updateContent('columns', initialColumns), 0);
      }
      
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <LayoutGrid className="w-4 h-4 text-pink-400" />
            <span className="text-xs font-medium text-slate-400">Columns Layout</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Layout</label>
              <Select
                value={component.content.layout || '2-equal'}
                onValueChange={(val) => {
                  const newColCount = getColumnCount(val);
                  const currentCols = component.content.columns || [];
                  let newCols;
                  
                  if (newColCount > currentCols.length) {
                    // Add new columns
                    newCols = [...currentCols];
                    for (let i = currentCols.length; i < newColCount; i++) {
                      newCols.push({ id: `col_${i}_${Date.now()}`, components: [] });
                    }
                  } else {
                    // Keep existing columns (don't lose content)
                    newCols = currentCols.slice(0, newColCount);
                  }
                  
                  onUpdate(component.id, {
                    ...component,
                    content: { ...component.content, layout: val, columns: newCols }
                  });
                }}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="2-equal">2 Equal</SelectItem>
                  <SelectItem value="3-equal">3 Equal</SelectItem>
                  <SelectItem value="4-equal">4 Equal</SelectItem>
                  <SelectItem value="1-2">1/3 + 2/3</SelectItem>
                  <SelectItem value="2-1">2/3 + 1/3</SelectItem>
                  <SelectItem value="1-1-2">1/4 + 1/4 + 1/2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Gap</label>
              <Select
                value={component.content.gap || 'md'}
                onValueChange={(val) => updateContent('gap', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Nested Column Editor */}
          <div className="border border-dashed border-slate-500 rounded-lg p-2 bg-slate-800">
            <p className="text-xs text-slate-400 mb-2 text-center">Drag components below into columns</p>
            <div className={`grid gap-2 ${
              component.content.layout === '3-equal' ? 'grid-cols-3' :
              component.content.layout === '4-equal' ? 'grid-cols-4' :
              component.content.layout === '1-1-2' ? 'grid-cols-4' :
              'grid-cols-2'
            }`}>
              {(component.content.columns || []).map((col, colIndex) => (
                <ColumnDropZone 
                  key={col.id}
                  column={col}
                  columnIndex={colIndex}
                  parentComponent={component}
                  onUpdate={onUpdate}
                  layout={component.content.layout}
                />
              ))}
            </div>
          </div>
        </div>
      );

    case 'html':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Code className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-medium text-slate-400">HTML Code</span>
          </div>
          <Textarea
            value={component.content.html || ''}
            onChange={(e) => updateContent('html', e.target.value)}
            placeholder="<div>Your HTML code...</div>"
            className="bg-slate-600 border-slate-500 text-white font-mono text-sm min-h-[100px]"
          />
        </div>
      );

    case 'tool':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-orange-400" />
            <span className="text-xs font-medium text-slate-400">Tool Plugin</span>
          </div>
          <Select
            value={component.content.tool || 'quick-50kb'}
            onValueChange={(val) => updateContent('tool', val)}
          >
            <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="quick-50kb">Quick 50KB Compressor</SelectItem>
              <SelectItem value="full-compressor">Full Image Compressor</SelectItem>
            </SelectContent>
          </Select>
          <div className="p-3 bg-slate-800 rounded-lg border border-dashed border-orange-500/50">
            <div className="flex items-center gap-2 text-xs text-orange-400">
              <Target className="w-4 h-4" />
              <span className="font-medium">
                {component.content.tool === 'full-compressor' 
                  ? 'Full Image Compressor' 
                  : 'Quick 50KB Compressor'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Tool will be fully functional on published page
            </p>
          </div>
        </div>
      );

    case 'ad':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium text-slate-400">Google AdSense Ad</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Ad Format</label>
              <Select
                value={component.content.format || 'auto'}
                onValueChange={(val) => updateContent('format', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="auto">Auto (Responsive)</SelectItem>
                  <SelectItem value="horizontal">Horizontal Banner</SelectItem>
                  <SelectItem value="vertical">Vertical Banner</SelectItem>
                  <SelectItem value="rectangle">Rectangle</SelectItem>
                  <SelectItem value="in-article">In-Article</SelectItem>
                  <SelectItem value="in-feed">In-Feed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Alignment</label>
              <Select
                value={component.content.align || 'center'}
                onValueChange={(val) => updateContent('align', val)}
              >
                <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Ad Slot ID (optional)</label>
            <Input
              value={component.content.slotId || ''}
              onChange={(e) => updateContent('slotId', e.target.value)}
              placeholder="1234567890"
              className="bg-slate-600 border-slate-500 text-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave empty for auto-generated slot. Find slot IDs in AdSense → Ads → By ad unit
            </p>
          </div>
          <div className="p-3 bg-slate-800 rounded-lg border border-dashed border-emerald-500/50">
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <DollarSign className="w-4 h-4" />
              <span className="font-medium">Ad Preview</span>
            </div>
            <div className="mt-2 p-4 bg-slate-700/50 rounded text-center text-xs text-slate-400">
              [Google AdSense Ad - {component.content.format || 'Auto'} format]
            </div>
          </div>
        </div>
      );

    default:
      return <div className="text-slate-400 text-sm">Unknown component type</div>;
  }
}

export default function PageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isNew = id === 'new';

  const [page, setPage] = useState({
    title: '',
    slug: '',
    components: [],
    seo: {
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      og_title: '',
      og_description: '',
      og_image: '',
      canonical_url: '',
      no_index: false,
      no_follow: false,
    },
    sidebar: {
      mode: 'global',
      position: null,
      width: null,
      widgets: []
    },
    status: 'draft'
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  useEffect(() => {
    if (!isNew) {
      fetchPage();
    }
  }, [id]);

  const fetchPage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pages/${id}`);
      if (response.ok) {
        const data = await response.json();
        setPage(data);
      } else {
        toast.error('Page not found');
        navigate('/admin/pages');
      }
    } catch (error) {
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!page.title || !page.slug) {
      toast.error('Title and slug are required');
      return;
    }

    setSaving(true);
    try {
      const url = isNew ? `${API_URL}/api/pages` : `${API_URL}/api/pages/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(page)
      });

      if (response.ok) {
        const savedPage = await response.json();
        toast.success(isNew ? 'Page created!' : 'Page saved!');
        if (isNew) {
          navigate(`/admin/pages/${savedPage.id}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save page');
      }
    } catch (error) {
      toast.error('Error saving page');
    } finally {
      setSaving(false);
    }
  };

  const addComponent = (type) => {
    const newComponent = {
      id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      content: {},
      style: {}
    };
    setPage(prev => ({ 
      ...prev, 
      components: [...prev.components, newComponent] 
    }));
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added`);
  };

  const updateComponent = (compId, updatedComp) => {
    setPage(prev => ({
      ...prev,
      components: prev.components.map(c => c.id === compId ? updatedComp : c)
    }));
  };

  const deleteComponent = (compId) => {
    setPage(prev => ({
      ...prev,
      components: prev.components.filter(c => c.id !== compId)
    }));
    toast.success('Component deleted');
  };

  const duplicateComponent = (component) => {
    // Deep clone the component with a new ID
    const clonedComponent = JSON.parse(JSON.stringify(component));
    clonedComponent.id = `${component.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // If it has nested columns with components, generate new IDs for those too
    if (clonedComponent.content?.columns) {
      clonedComponent.content.columns = clonedComponent.content.columns.map(col => ({
        ...col,
        id: `col_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        components: (col.components || []).map(nestedComp => ({
          ...nestedComp,
          id: `nested_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }))
      }));
    }
    
    // If it has nested children (for sections), generate new IDs
    if (clonedComponent.content?.children) {
      clonedComponent.content.children = clonedComponent.content.children.map(child => ({
        ...child,
        id: `nested_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    
    // Find the index of the original component and insert the clone after it
    setPage(prev => {
      const index = prev.components.findIndex(c => c.id === component.id);
      const newComponents = [...prev.components];
      newComponents.splice(index + 1, 0, clonedComponent);
      return { ...prev, components: newComponents };
    });
    
    toast.success('Component duplicated');
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (over && active.id !== over.id) {
      setPage(prev => {
        const oldIndex = prev.components.findIndex(c => c.id === active.id);
        const newIndex = prev.components.findIndex(c => c.id === over.id);
        return {
          ...prev,
          components: arrayMove(prev.components, oldIndex, newIndex)
        };
      });
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  const contentComponents = COMPONENT_TYPES.filter(c => c.category === 'content');
  const layoutComponents = COMPONENT_TYPES.filter(c => c.category === 'layout');

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      <Toaster position="top-center" theme="dark" />
      
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/pages')} className="text-slate-400 hover:text-white p-2 hover:bg-slate-700 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Input
              value={page.title}
              onChange={(e) => setPage({ ...page, title: e.target.value })}
              placeholder="Page title..."
              className="bg-slate-700 border-slate-600 text-white text-lg font-semibold w-64"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={page.status} onValueChange={(val) => setPage({ ...page, status: val })}>
              <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            
            {page.status === 'published' && page.slug && (
              <a
                href={`/${page.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
              >
                <Eye className="w-5 h-5" />
              </a>
            )}
            
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Components */}
        <aside className="w-72 bg-slate-800 border-r border-slate-700 overflow-y-auto flex-shrink-0">
          <Tabs defaultValue="components" className="h-full flex flex-col">
            <TabsList className="w-full bg-slate-700 rounded-none border-b border-slate-600 flex-shrink-0">
              <TabsTrigger value="components" className="flex-1 data-[state=active]:bg-slate-600">Add</TabsTrigger>
              <TabsTrigger value="seo" className="flex-1 data-[state=active]:bg-slate-600">SEO</TabsTrigger>
              <TabsTrigger value="sidebar" className="flex-1 data-[state=active]:bg-slate-600">
                <PanelLeft className="w-3 h-3 mr-1" />
                Sidebar
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="components" className="flex-1 overflow-y-auto p-4 space-y-4 m-0">
              {/* Content Components */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Content</p>
                <div className="space-y-1">
                  {contentComponents.map((comp) => (
                    <button
                      key={comp.type}
                      onClick={() => addComponent(comp.type)}
                      className="w-full flex items-center gap-3 p-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-left transition-all hover:translate-x-1"
                    >
                      <comp.icon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span className="text-sm text-white">{comp.label}</span>
                      <Plus className="w-3 h-3 text-slate-500 ml-auto" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Layout Components */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Layout & Spacing</p>
                <div className="space-y-1">
                  {layoutComponents.map((comp) => (
                    <button
                      key={comp.type}
                      onClick={() => addComponent(comp.type)}
                      className="w-full flex items-center gap-3 p-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-left transition-all hover:translate-x-1"
                    >
                      <comp.icon className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span className="text-sm text-white">{comp.label}</span>
                      <Plus className="w-3 h-3 text-slate-500 ml-auto" />
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="seo" className="flex-1 overflow-y-auto p-4 space-y-4 m-0">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">URL Slug</label>
                <Input
                  value={page.slug}
                  onChange={(e) => setPage({ ...page, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="page-url-slug"
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">URL: /{page.slug || 'your-slug'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Meta Title</label>
                <Input
                  value={page.seo.meta_title || ''}
                  onChange={(e) => setPage({ ...page, seo: { ...page.seo, meta_title: e.target.value } })}
                  placeholder="SEO title"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Meta Description</label>
                <Textarea
                  value={page.seo.meta_description || ''}
                  onChange={(e) => setPage({ ...page, seo: { ...page.seo, meta_description: e.target.value } })}
                  placeholder="SEO description"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Keywords</label>
                <Input
                  value={page.seo.meta_keywords || ''}
                  onChange={(e) => setPage({ ...page, seo: { ...page.seo, meta_keywords: e.target.value } })}
                  placeholder="keyword1, keyword2"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">OG Image URL</label>
                <Input
                  value={page.seo.og_image || ''}
                  onChange={(e) => setPage({ ...page, seo: { ...page.seo, og_image: e.target.value } })}
                  placeholder="https://..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <label className="text-xs text-slate-400">No Index</label>
                <Switch
                  checked={page.seo.no_index || false}
                  onCheckedChange={(val) => setPage({ ...page, seo: { ...page.seo, no_index: val } })}
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <label className="text-xs text-slate-400">No Follow</label>
                <Switch
                  checked={page.seo.no_follow || false}
                  onCheckedChange={(val) => setPage({ ...page, seo: { ...page.seo, no_follow: val } })}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="sidebar" className="flex-1 overflow-y-auto p-4 m-0">
              <PageSidebarSettings
                sidebarSettings={page.sidebar || { mode: 'global', position: null, width: null, widgets: [] }}
                onChange={(newSidebar) => setPage({ ...page, sidebar: newSidebar })}
              />
            </TabsContent>
          </Tabs>
        </aside>

        {/* Canvas */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Page Content</h2>
              <span className="text-xs text-slate-500">{page.components.length} components</span>
            </div>
            
            {page.components.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-700 rounded-xl bg-slate-800/50">
                <Plus className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400 mb-2">No components yet</p>
                <p className="text-slate-500 text-sm">Click components in the sidebar to add them</p>
              </div>
            ) : (
              <DndContext 
                sensors={sensors} 
                collisionDetection={closestCenter} 
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={page.components.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  {page.components.map((component) => (
                    <SortableComponent
                      key={component.id}
                      component={component}
                      onUpdate={updateComponent}
                      onDelete={deleteComponent}
                      onDuplicate={duplicateComponent}
                      isActive={activeId === component.id}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
